import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';

import { cn } from '../../lib/cn';
import styles from './Table.module.css';

export type SortDirection = 'asc' | 'desc';

export type TableColumn<T> = {
  readonly id: string;
  readonly header: string;
  readonly render: (row: T) => ReactNode;
  readonly width?: string;
  readonly align?: 'start' | 'end' | 'center';
  readonly sortable?: boolean;
};

export type TableProps<T> = {
  readonly columns: readonly TableColumn<T>[];
  readonly rows: readonly T[];
  readonly getRowId: (row: T) => string;
  readonly sort?: { readonly columnId: string; readonly direction: SortDirection } | null;
  readonly onSortChange?: (columnId: string) => void;
  readonly onRowClick?: (row: T) => void;
  readonly emptyState?: ReactNode;
  readonly selection?: {
    readonly selectedIds: readonly string[];
    readonly onChange: (ids: readonly string[]) => void;
  };
  readonly ariaLabel?: string;
};

/** Плотность строки 36 px (docs/06 §5.8). */
export function Table<T>({
  columns,
  rows,
  getRowId,
  sort,
  onSortChange,
  onRowClick,
  emptyState,
  selection,
  ariaLabel,
}: TableProps<T>) {
  const allSelected = selection !== undefined && rows.length > 0 && rows.every((row) => selection.selectedIds.includes(getRowId(row)));

  const toggleAll = (): void => {
    if (!selection) {
      return;
    }
    selection.onChange(allSelected ? [] : rows.map(getRowId));
  };

  const toggleRow = (id: string): void => {
    if (!selection) {
      return;
    }
    selection.onChange(
      selection.selectedIds.includes(id)
        ? selection.selectedIds.filter((item) => item !== id)
        : [...selection.selectedIds, id],
    );
  };

  if (rows.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <table className={styles['table']} aria-label={ariaLabel}>
      <thead>
        <tr>
          {selection ? (
            <th className={styles['selectCell']}>
              <input
                type="checkbox"
                checked={allSelected}
                aria-label="Выбрать все строки"
                onChange={toggleAll}
              />
            </th>
          ) : null}
          {columns.map((column) => {
            const isSorted = sort?.columnId === column.id;
            return (
              <th
                key={column.id}
                scope="col"
                style={column.width === undefined ? undefined : { width: column.width }}
                className={cn(styles['th'], styles[column.align ?? 'start'])}
                aria-sort={isSorted ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                {column.sortable && onSortChange ? (
                  <button type="button" className={styles['sortButton']} onClick={() => onSortChange(column.id)}>
                    {column.header}
                    {isSorted ? (
                      sort.direction === 'asc' ? (
                        <ArrowUp aria-hidden size={12} />
                      ) : (
                        <ArrowDown aria-hidden size={12} />
                      )
                    ) : (
                      <ChevronsUpDown aria-hidden size={12} />
                    )}
                  </button>
                ) : (
                  column.header
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const id = getRowId(row);
          return (
            <tr
              key={id}
              className={cn(styles['row'], onRowClick && styles['clickable'])}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {selection ? (
                <td className={styles['selectCell']} onClick={(event) => event.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selection.selectedIds.includes(id)}
                    aria-label={`Выбрать строку ${id}`}
                    onChange={() => toggleRow(id)}
                  />
                </td>
              ) : null}
              {columns.map((column) => (
                <td key={column.id} className={cn(styles['td'], styles[column.align ?? 'start'])}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
